import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './ui/accordion';
import { Question } from '@phosphor-icons/react';

const TroubleshootHelpModal = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="troubleshoot-help-modal">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <Question weight="duotone" className="text-amber-500" />
            Having Issues?
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          If things look outdated or something isn't loading, a quick refresh usually fixes it. Find your device below:
        </p>
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="quick">
            <AccordionTrigger className="text-sm font-semibold">Try This First</AccordionTrigger>
            <AccordionContent>
              <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-4">
                <li>Close TurfPulse completely and reopen it.</li>
                <li>If you see a <strong>"New update available"</strong> banner, tap <strong>Refresh Now</strong>.</li>
              </ul>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="iphone">
            <AccordionTrigger className="text-sm font-semibold">iPhone / iPad (Safari)</AccordionTrigger>
            <AccordionContent>
              <ol className="space-y-1 text-sm text-muted-foreground list-decimal pl-4">
                <li>Tap the <strong>Share</strong> button (box with arrow).</li>
                <li>Scroll down and tap <strong>"Clear History and Website Data"</strong>.</li>
                <li>Or: <strong>Settings &gt; Safari &gt; Clear History and Website Data</strong>.</li>
                <li>Revisit <strong>getturfpulse.com</strong>.</li>
              </ol>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="android">
            <AccordionTrigger className="text-sm font-semibold">Android (Chrome)</AccordionTrigger>
            <AccordionContent>
              <ol className="space-y-1 text-sm text-muted-foreground list-decimal pl-4">
                <li>Tap the <strong>three dots menu</strong> &gt; Settings &gt; Privacy.</li>
                <li>Tap <strong>Clear Browsing Data</strong>.</li>
                <li>Select <strong>"Cached images and files"</strong> then tap Clear.</li>
                <li>Revisit <strong>getturfpulse.com</strong>.</li>
              </ol>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="samsung">
            <AccordionTrigger className="text-sm font-semibold">Samsung Internet</AccordionTrigger>
            <AccordionContent>
              <ol className="space-y-1 text-sm text-muted-foreground list-decimal pl-4">
                <li>Tap the <strong>hamburger menu</strong> &gt; Settings &gt; Privacy.</li>
                <li>Tap <strong>Delete Browsing Data</strong>.</li>
                <li>Check <strong>"Cache"</strong> then tap Delete.</li>
                <li>Revisit <strong>getturfpulse.com</strong>.</li>
              </ol>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="desktop">
            <AccordionTrigger className="text-sm font-semibold">Desktop Browser</AccordionTrigger>
            <AccordionContent>
              <ul className="space-y-1 text-sm text-muted-foreground list-disc pl-4">
                <li><strong>Windows / Linux:</strong> Ctrl + Shift + R</li>
                <li><strong>Mac:</strong> Cmd + Shift + R</li>
              </ul>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="pwa">
            <AccordionTrigger className="text-sm font-semibold">PWA (Home Screen Icon)</AccordionTrigger>
            <AccordionContent>
              <ol className="space-y-1 text-sm text-muted-foreground list-decimal pl-4">
                <li>Long-press the TurfPulse icon and <strong>Remove / Delete</strong>.</li>
                <li>Go back to <strong>getturfpulse.com</strong> in your browser.</li>
                <li>Tap <strong>Share</strong> then <strong>"Add to Home Screen"</strong> to reinstall.</li>
              </ol>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </DialogContent>
    </Dialog>
  );
};

export const TroubleshootHelpButton = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 opacity-70 hover:opacity-100"
        data-testid="having-issues-btn"
      >
        <Question weight="bold" size={14} />
        Having issues? Tap here for help
      </button>
      <TroubleshootHelpModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};
